import _ from 'lodash'
import $ from 'jquery'
import Backbone from 'backbone'
import constants from 'scoreboard/components/constants'
import deviceType from 'scoreboard/components/device-type'
import nodeWidth from 'scoreboard/components/node-width'

/**
 * This component handles the expanding and contracting of trees so that node
 * contents are fully visible. This component differs from the object-selector-expand-collapse
 * component (object-selector-expand-collapse.js) because this component works on
 * hover, not node selection and is meant to work with standalone trees (ex. nav pane, admin groups).
 *
 * Example:
 *  this.navPaneExpander = new TreeHoverExpandContract()
 *  this.navPaneExpander.init({
 *  	el: this.navPane,
 *  	canExpandTree:  () => this.preventNavExpand,
 *  	expandMargin: () => this.headPane.width(),
 *  	expandConstraint: () => $('body').width() - 50
 *  })
 *
 * @param {Object} el The container containing the tree that needs the potential expanding.
 * The actual expanded width will be applied to this element.
 * @param {Function} [options.canExpandTree] A function to be called when determining whether the tree should be expandable or not.
 * @param {Function} [options.canExpandTree] A function to be called when determining whether the tree should be contractable or not.
 * @param {Function} [options.expandConstraint] A function which returns the outer-most constraint to the expanding container. In other words, what is
 * the max width the container can expand to according to where it's located?
 * @param {Function} [options.expandMargin] A function which returns an extra margin to consider when calculating the expanded container width.
 * @param {String} [options.treeContractBoundary] A class which represents the area which, when the mouse leaves it, triggers the contracting of the tree.
 * This defaults to .tree-container if not defined.
 * @param {Boolean} [options.clearWidthAfterContract] Determines whether the width of the tree should persist after the
 * expand/contract is over. This defaults to true.
 */

const TreeHoverExpandContract = Backbone.View.extend({
	initialize: function(options = {}) {
		this.options = options

		if (_.isNil(this.options.el)) {
			throw new Error('a tree container must be specified to expand/contract')
		}

		if (_.isNil(this.options.clearWidthAfterContract)) {
			this.options.clearWidthAfterContract = true
		}

		this.$el.addClass('tree-hover-expand-contract')

		// When a node is hovered over in the container, kick off the possible expand.
		this.$el.on('mouseenter', '.node', e => {
			// If we can expand the tree, go ahead and do it. Use a provided
			// canExpandTree method if it was provided.
			if (this.canExpandTree(e)) {
				this.expandTree(e)
			}
		})

		// Figure out where we need to mouseleave in order to contract the container and listen for it.
		this.$el.on('mouseleave', this.options.treeContractBoundary || '.tree-container', () => {
			if (!_.result(this.options, 'canContractTree', true)) {
				return
			}
			this.contractTree()
		})

		return this
	},

	canExpandTree: function(e) {
		// If custom logic was passed in to determine the expandability of the tree,
		// see if that passses first
		if (!_.result(this.options, 'canExpandTree', true)) {
			return false
		}

		// Here's how we know not to expand the tree:
		// - There's no tree expanding on touch-only devices.
		// - The container will have the 'is-expanding' class meaning it's in the middle of expanding.
		// - The container will have the 'is-contracting' class meaning it's in the middle of contracting
		// - The node we're hovering over is a placeholder node (one which is being dragged)
		// NOTE: Why is .is-expanded not tested here? Well that's because we don't want to
		// miss expanding when there are new tree nodes present since last expanding
		// (eg. Expanding a node which reveals other nodes which overflow more than the parent)
		if (deviceType.touchOnly
			|| this.$el.hasClass('is-expanding')
			|| this.$el.hasClass('is-contracting')
			|| $(e.target).hasClass('placeholder')) {
			return false
		}

		return true
	},

	canContractTree: function() {
		// If custom logic was passed in to determine the contractability of the tree,
		// see if that passses first
		if (!_.result(this.options, 'canContractTree', true)) {
			return false
		}

		// Here's how we know not to contract the tree:
		// - There's no tree contracting on touch-only devices.
		// - The container will have the 'is-contracting' class meaning it's in the middle of contracting.
		// - The container must have either the 'is-expanding' or 'is-expanded' class.
		if (deviceType.touchOnly
			|| this.$el.hasClass('is-contracting')
			|| !(this.$el.hasClass('is-expanding') || this.$el.hasClass('is-expanded'))) {
			return false
		}

		return true
	},

	expandTree: function(e) {
		var target = $(e.currentTarget)
		var expandNavTreeTimer

		// Check to make sure we can expand before doing anything
		if (!this.canExpandTree(e)) {
			return
		}

		// In order to run the expand multiple times before contracting it, we need
		// to capture the current width so we can transition from it.
		this.width = this.$el.outerWidth()
		// If an event didn't trigger the method (i.e. The org-pane expand), we will
		// set the originalWidth immediately. Otherwise, the originalWidth should be
		// set after the delay.
		if (_.isNil(e)) {
			this.originalWidth = this.width
		}
		// Seems redundant, but needs a hard set width for transition to work
		this.$el.css('width', this.width)

		var expand = () => {
			// Only expand if a single node has been hovered for half a second without any mouse movement.
			expandNavTreeTimer = setTimeout(() => {
				// Double check here to make sure we should be expanding. The user could have
				// clicked and held on the node, making it a placholder (dragged) node, in which
				// case we do not want to do any expanding of the container.
				if (!this.canExpandTree(e)) {
					clearTimeout(expandNavTreeTimer)
					return
				}

				let constraint = _.invoke(this.options, 'expandConstraint', target) || 0
				let margin = _.invoke(this.options, 'expandMargin') || 0
				let totalNodeWidth = Math.min(nodeWidth(target), constraint - margin)
				if (totalNodeWidth > this.width) {
					this.originalWidth = this.originalWidth || this.width
					this.$el.addClass('is-expanding').css('width', totalNodeWidth)
					this.trigger('startExpand', totalNodeWidth, this.originalWidth)

					// After the container has been expanded, tack on the 'is-expanded' class and remove the 'is-expanding' class.
					this.$el.one('transitionend', () => {
						this.$el
							.removeClass('is-expanding')
							.addClass('is-expanded')
					})
				}
			}, constants.TREE_EXPAND_DELAY)
		}
		expand()

		// If the mouse moves within the node before the setTimeout's function runs, reset the expand timer.
		target.on('mousemove', () => {
				if (!this.canExpandTree(e)) {
					return
				}
				clearTimeout(expandNavTreeTimer)
				// Update this.width when we move the mouse within the target so
				// we can do our calculations in expand() with the correct (new) tree width
				// https://github.com/SpiderStrategies/Scoreboard/issues/29182
				this.width = this.$el.outerWidth()
				expand()
			})
			// If the mouse leaves the node before the setTimeout's function runs, cancel the expand.
			.on('mouseleave click', () => clearTimeout(expandNavTreeTimer))
	},

	contractTree: function(delayCollapse) {
		if (!this.canContractTree()) {
			return
		}

		this.$el.addClass('is-contracting')
		let collapseTime = delayCollapse ? constants.TREE_CONTRACT_DELAY : 0

		setTimeout(() => {
			if (delayCollapse) {
				this.$el.addClass('is-delaying-collapse')
			}

			this.trigger('startContract')
			this.$el
				.removeClass('is-expanding')
				.removeClass('is-expanded')
				.css('width', this.originalWidth)

			// After the container is brought back to its original width, we need
			// to nix the width style property we just appended to it so that it doesn't
			// get stuck with that width from now on.
			this.$el.one('transitionend', () => {
				this.$el.removeClass('is-contracting is-delaying-collapse')

				if (this.options.clearWidthAfterContract) {
					this.$el.css('width', '')
				}

				this.trigger('endContract')
			})

			if (_.isNil(delayCollapse)) {
				delete this.originalWidth
			}
		}, collapseTime)
	}
})

export default TreeHoverExpandContract

TreeHoverExpandContract.__filename__ = 'components/tree-hover-expand-contract.js'
